/**
 * Age verification utilities for COPPA compliance
 * Ensures proper handling of under-13 users and parental consent
 */

export interface AgeVerificationResult {
  isUnder13: boolean;
  requiresParentalConsent: boolean;
  age: number;
  canProceed: boolean;
  message?: string;
}

export interface ParentalConsentData {
  parentEmail: string;
  parentName: string;
  consentGiven: boolean;
  consentDate: string;
  verificationCode?: string;
}

/**
 * Verify age and determine if parental consent is required
 * @param birthdate - ISO date string (YYYY-MM-DD)
 * @returns Age verification result
 */
export const verifyAge = (birthdate: string): AgeVerificationResult => {
  if (!birthdate) {
    return {
      isUnder13: false,
      requiresParentalConsent: false,
      age: 0,
      canProceed: false,
      message: 'Birth date is required'
    };
  }

  const today = new Date();
  const birth = new Date(birthdate);
  
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  // If birthday hasn't occurred this year yet, subtract 1
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  const isUnder13 = age < 13;
  const requiresParentalConsent = isUnder13;

  return {
    isUnder13,
    requiresParentalConsent,
    age,
    canProceed: !isUnder13, // Only allow over-13 users to proceed without parental consent
    message: isUnder13 
      ? 'Users under 13 require parental consent to create an account'
      : 'Age verification successful'
  };
};

/**
 * Generate a verification code for parental consent
 * @returns 6-digit verification code
 */
export const generateParentalConsentCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

/**
 * Validate parental consent data
 * @param consentData - Parental consent information
 * @returns Validation result
 */
export const validateParentalConsent = (consentData: Partial<ParentalConsentData>): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!consentData.parentEmail || !consentData.parentEmail.trim()) {
    errors.push('Parent email is required');
  } else if (!isValidEmail(consentData.parentEmail)) {
    errors.push('Parent email must be valid');
  }

  if (!consentData.parentName || !consentData.parentName.trim()) {
    errors.push('Parent name is required');
  }

  if (!consentData.consentGiven) {
    errors.push('Parental consent must be given');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Check if email is valid
 * @param email - Email to validate
 * @returns True if valid email
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Get age-appropriate data collection notice
 * @param age - User's age
 * @returns Data collection notice text
 */
export const getDataCollectionNotice = (age: number): string => {
  if (age < 13) {
    return `We collect limited information to provide our music lesson service. For users under 13, we require parental consent and only collect information necessary for the service. We do not share personal information with third parties without parental consent.`;
  } else if (age < 16) {
    return `We collect information to provide our music lesson service. For users under 16, we may require parental consent for certain features. We protect your privacy and only use your information as described in our privacy policy.`;
  } else {
    return `We collect information to provide our music lesson service. We protect your privacy and only use your information as described in our privacy policy.`;
  }
};

/**
 * Get parental consent requirements text
 * @returns Parental consent requirements
 */
export const getParentalConsentRequirements = (): string => {
  return `For users under 13, we require verifiable parental consent before collecting any personal information. This includes:
  
• Parent/guardian email verification
• Written consent to data collection
• Right to review and delete child's data
• Notification of any material changes to data practices

By providing consent, you acknowledge that you are the parent/guardian of this child and have the legal right to provide consent on their behalf.`;
};
